import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  ArrowsLeftRight, Wallet, CheckCircle, WarningCircle, Clock, Plus, CaretDown,
  User as UserIcon, IdentificationCard,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fmt2 = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || '—'; } };

// Auto-format thousands separators in numeric string preserving the user's decimals.
const formatAmountInput = (raw) => {
  const s = String(raw || '').replace(/[^\d.]/g, '');
  if (!s) return '';
  const parts = s.split('.');
  const intPart = parts[0].replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const decPart = parts[1] !== undefined ? parts[1].slice(0, 2) : undefined;
  return decPart !== undefined ? `${intPart || '0'}.${decPart}` : (intPart || '');
};
const parseAmountInput = (formatted) => {
  const cleaned = String(formatted || '').replace(/,/g, '');
  const n = parseFloat(cleaned);
  return isFinite(n) ? n : 0;
};

const STATUS_META = {
  pending: { icon: Clock, tone: 'text-[#F59E0B]', bg: 'bg-[#FEF3C7]', label: 'Pending' },
  success: { icon: CheckCircle, tone: 'text-[#10B981]', bg: 'bg-[#DCFCE7]', label: 'Success' },
  failed:  { icon: WarningCircle, tone: 'text-[#EF4444]', bg: 'bg-[#FEE2E2]', label: 'Failed' },
};

// A single card lookup input row with debounced auto-validate.
function CardLookup({ label, cardNo, setCardNo, sub, loading, setSub, setLoading, testid, previewBalance }) {
  // Auto-validate as the user types (debounced), no button needed.
  useEffect(() => {
    const cn = cardNo.trim();
    setSub(null);
    if (cn.length < 4) return;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/inter-wallets/lookup`, {
          params: { card_no: cn },
          withCredentials: true, headers: authHeaders(),
        });
        if (cancelled) return;
        if (data?.found) setSub(data.subscriber);
        else setSub({ _notFound: true, message: data?.message || 'Not found' });
      } catch (e) {
        if (!cancelled) setSub({ _notFound: true, message: formatApiError(e, 'Lookup failed') });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); setLoading(false); };
  }, [cardNo]);

  return (
    <div className="space-y-1.5" data-testid={testid}>
      <Label>{label} <span className="text-[#EF4444]">*</span></Label>
      <div className="relative">
        <Input
          value={cardNo}
          onChange={(e) => setCardNo(e.target.value)}
          placeholder="e.g. 1360009195611"
          className="border-[#EDE6D8] rounded-lg font-mono pr-9"
          data-testid={`${testid}-input`}
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-[#BAE6FD] border-t-[#0369A1] rounded-full animate-spin" data-testid={`${testid}-spinner`} />
        )}
      </div>
      {sub && !sub._notFound && (
        <div className="p-3 mt-1 bg-[#F0F9FF] border border-[#BAE6FD] rounded-lg" data-testid={`${testid}-info`}>
          <div className="flex items-center gap-2 text-sm">
            <UserIcon size={14} className="text-[#0369A1]" />
            <span className="font-semibold text-[#0C4A6E]">{sub.sub_name || '(no name)'}</span>
            <Badge className="bg-white border border-[#BAE6FD] text-[#0C4A6E] text-[10px]">{sub.entity_name}</Badge>
          </div>
          <div className="text-xs text-[#0369A1] mt-1 font-mono">Card {sub.card_no} · Sub #{sub.sub_id}</div>
          <div className="text-xs mt-1.5 flex items-center gap-3">
            <span>
              <span className="text-[#78716C]">Balance:</span>{' '}
              <span className="font-bold text-[#0C4A6E]">{fmt2(sub.wallet_bal)} {sub.currency}</span>
            </span>
            {previewBalance !== undefined && previewBalance !== null && (
              <span>
                <span className="text-[#78716C]">→ After:</span>{' '}
                <span className={`font-bold ${previewBalance < 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>
                  {fmt2(previewBalance)} {sub.currency}
                </span>
              </span>
            )}
          </div>
        </div>
      )}
      {sub && sub._notFound && (
        <div className="p-2 mt-1 bg-[#FEF2F2] border border-[#FECACA] rounded-lg text-xs text-[#991B1B] flex items-center gap-1.5" data-testid={`${testid}-error`}>
          <WarningCircle size={13} weight="fill" />
          {sub.message}
        </div>
      )}
    </div>
  );
}

export default function InterWalletsPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // transfer dialog state
  const [open, setOpen] = useState(false);
  const [amountStr, setAmountStr] = useState('');
  const [fromCard, setFromCard] = useState('');
  const [fromSub, setFromSub] = useState(null);
  const [fromLoading, setFromLoading] = useState(false);
  const [toCard, setToCard] = useState('');
  const [toSub, setToSub] = useState(null);
  const [toLoading, setToLoading] = useState(false);
  const [narration, setNarration] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const amount = useMemo(() => parseAmountInput(amountStr), [amountStr]);

  const fromPreview = useMemo(() => {
    if (!fromSub || fromSub._notFound || !amount) return undefined;
    return fromSub.wallet_bal - amount;
  }, [fromSub, amount]);
  const toPreview = useMemo(() => {
    if (!toSub || toSub._notFound || !amount) return undefined;
    return toSub.wallet_bal + amount;
  }, [toSub, amount]);

  const insufficientBalance = fromSub && !fromSub._notFound && amount > 0 && fromSub.wallet_bal < amount;
  const sameCard = fromCard.trim() && toCard.trim() && fromCard.trim() === toCard.trim();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/inter-wallets/logs`, {
        params: { limit: pageSize, skip: (page - 1) * pageSize },
        withCredentials: true, headers: authHeaders(),
      });
      setRows(data?.rows || []);
      setTotal(data?.total || 0);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load logs'));
      setRows([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setAmountStr(''); setFromCard(''); setFromSub(null);
    setToCard(''); setToSub(null); setNarration('');
  };

  const openTransfer = () => { resetForm(); setOpen(true); };

  const submit = async () => {
    if (!(amount > 0)) { toast.error('Enter a positive amount'); return; }
    if (!fromSub || fromSub._notFound) { toast.error('Validate the source card first'); return; }
    if (!toSub || toSub._notFound) { toast.error('Validate the destination card first'); return; }
    if (sameCard) { toast.error('From and To must be different cards'); return; }
    if (insufficientBalance) { toast.error('Insufficient balance on source card'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/inter-wallets/transfer`, {
        from_card_no: fromCard.trim(), to_card_no: toCard.trim(),
        amount, narration: narration.trim() || undefined,
      }, { withCredentials: true, headers: authHeaders() });
      toast.success(data?.message || 'Transfer completed');
      setOpen(false); load();
    } catch (e) {
      // 501 = local pending row created but upstream endpoint not enabled yet.
      const detail = e?.response?.data?.detail || '';
      if (e?.response?.status === 501) {
        toast.warning(detail);
        setOpen(false); load();
      } else {
        toast.error(formatApiError(e, 'Transfer failed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-6 space-y-6" data-testid="inter-wallets-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Inter Wallets
          </h1>
          <p className="text-sm text-[#78716C] mt-1">
            Move funds between subscriber wallets. All transfers are logged for audit.
          </p>
        </div>
        <Button
          onClick={openTransfer}
          className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
          data-testid="iw-new-transfer-btn"
        >
          <Plus size={14} className="mr-2" weight="bold" />
          New Inter-Wallet Transfer
        </Button>
      </div>

      <div className="bg-white border border-[#EDE6D8] rounded-lg overflow-hidden" data-testid="iw-log-card">
        <div className="px-4 py-3 border-b border-[#EDE6D8] bg-[#FCFAF6] flex items-center justify-between">
          <div className="text-sm font-semibold text-[#0A2540] flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <ArrowsLeftRight size={16} /> Transfer log
          </div>
          <div className="text-xs text-[#78716C]" data-testid="iw-total-count">
            {loading ? 'Loading…' : `${total} transfer${total === 1 ? '' : 's'}`}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#EDE6D8] bg-white text-left">
                {['When', 'Ref', 'From', 'To', 'Amount', 'Status', 'By'].map(h => (
                  <th key={h} className="px-4 py-2.5 text-[10px] uppercase tracking-wider text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EDE6D8]">
              {loading && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-xs text-[#78716C]">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-[#78716C]" data-testid="iw-empty">
                    <Wallet size={22} className="mx-auto mb-2 opacity-60" />
                    No inter-wallet transfers yet.
                  </td>
                </tr>
              )}
              {!loading && rows.map(r => {
                const st = STATUS_META[r.status] || STATUS_META.pending;
                const StatusIcon = st.icon;
                return (
                  <tr key={r.ref} className="hover:bg-[#FCFAF6]/50" data-testid={`iw-row-${r.ref}`}>
                    <td className="px-4 py-3 text-xs text-[#78716C] whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3 text-xs font-mono text-[#0A2540]">{r.ref}</td>
                    <td className="px-4 py-3 text-xs">
                      <div className="font-medium text-[#1C1917]">{r.from_sub_name || '—'}</div>
                      <div className="text-[10px] text-[#78716C] font-mono">{r.from_card_no}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div className="font-medium text-[#1C1917]">{r.to_sub_name || '—'}</div>
                      <div className="text-[10px] text-[#78716C] font-mono">{r.to_card_no}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                      <div className="font-bold text-[#0A2540]">{fmt2(r.amount)} {r.currency}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${st.bg} ${st.tone}`}>
                        <StatusIcon size={11} weight="fill" /> {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#78716C]">{r.initiated_by_username || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-[#EDE6D8] text-xs">
            <div className="text-[#78716C]">Page {page} of {totalPages}</div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="rounded-md h-7 px-2 text-xs">Prev</Button>
              <Button size="sm" variant="outline" disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="rounded-md h-7 px-2 text-xs">Next</Button>
            </div>
          </div>
        )}
      </div>

      {/* Transfer dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="iw-transfer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowsLeftRight size={20} weight="fill" className="text-[#0A2540]" />
              New inter-wallet transfer
            </DialogTitle>
            <DialogDescription>
              Move funds between two subscriber wallets. Both cards must exist and belong to the same entity when you&apos;re not a super admin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Amount <span className="text-[#EF4444]">*</span></Label>
              <Input
                inputMode="decimal"
                value={amountStr}
                onChange={(e) => setAmountStr(formatAmountInput(e.target.value))}
                placeholder="0.00"
                className="border-[#EDE6D8] rounded-lg text-lg font-bold text-[#0A2540]"
                data-testid="iw-amount"
              />
            </div>

            <CardLookup
              label="Transfer from (Card #)"
              cardNo={fromCard} setCardNo={setFromCard}
              sub={fromSub} setSub={setFromSub}
              loading={fromLoading} setLoading={setFromLoading}
              testid="iw-from"
              previewBalance={fromPreview}
            />

            <div className="flex justify-center">
              <CaretDown size={18} className="text-[#78716C]" />
            </div>

            <CardLookup
              label="To (Card #)"
              cardNo={toCard} setCardNo={setToCard}
              sub={toSub} setSub={setToSub}
              loading={toLoading} setLoading={setToLoading}
              testid="iw-to"
              previewBalance={toPreview}
            />

            <div className="space-y-1.5">
              <Label>Narration <span className="text-[10px] text-[#78716C] font-normal">optional</span></Label>
              <Input
                value={narration}
                onChange={(e) => setNarration(e.target.value)}
                placeholder="e.g. correction — mistaken top-up"
                className="border-[#EDE6D8] rounded-lg"
                data-testid="iw-narration"
              />
            </div>

            {(insufficientBalance || sameCard) && (
              <div className="p-2.5 bg-[#FEF2F2] border border-[#FECACA] rounded-lg text-xs text-[#991B1B] flex items-center gap-1.5" data-testid="iw-form-error">
                <WarningCircle size={13} weight="fill" />
                {sameCard
                  ? 'From and To must be different cards.'
                  : `Source balance (${fmt2(fromSub?.wallet_bal)}) is less than the amount (${fmt2(amount)}).`}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-lg" data-testid="iw-cancel">Cancel</Button>
            <Button
              onClick={submit}
              disabled={submitting || !(amount > 0) || !fromSub || fromSub._notFound || !toSub || toSub._notFound || insufficientBalance || sameCard}
              className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
              data-testid="iw-submit"
            >
              <IdentificationCard size={14} className="mr-2" />
              {submitting ? 'Transferring…' : 'Submit transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
